// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
// See https://jira.tamg.io/browse/WP-2462
import * as React from 'react';
import AutoFocus from './AutoFocus';

type FocusTrapType = {
  onFocus?: () => mixed,
  tabIndex?: number,
  children?: React.Node,
};

type Props = {|
  /** If true, doe not focus on mount. */
  disableFocusOnMount?: boolean,
  /** If true, will not serve as an anchor point to restore focus to on unmount of a descendant focus */
  disableFocusRestorePoint?: boolean,
  /** If true, will not restore focus to a previous point */
  disableRestoreFocus?: boolean,
  /** An element type; either an jsx intrinsic or a React component, which takes in onFocus, tabIndex, and forwards its ref */
  type?: string | React.ComponentType<$Exact<FocusTrapType>>,
  /** The content node to render */
  children: React.Node,
|};

/** Traps interactive elements within the provided element types. If there are no interactive elements, then this is a no-op */
const FocusTrap = ({
  disableFocusOnMount,
  disableFocusRestorePoint,
  disableRestoreFocus,
  type,
  children,
}: Props) => {
  const start = React.useRef(null);
  const end = React.useRef(null);

  const isFocusable = React.useCallback((element: HTMLElement) => {
    if (!element) {
      return false;
    }
    if ((element: any).disabled) {
      return false;
    }
    if (element === start.current || element === end.current) {
      return false;
    }
    // The next are according to the
    if (
      element.tabIndex > 0 ||
      (element.tabIndex === 0 && element.getAttribute('tabIndex') !== null)
    ) {
      return true;
    }

    switch (element.nodeName) {
      case 'A': {
        if (element instanceof HTMLAnchorElement) {
          return element.href && element.rel !== 'ignore';
        }
        break;
      }
      case 'INPUT': {
        if (element instanceof HTMLInputElement) {
          return element.type !== 'hidden' && element.type !== 'file';
        }
        break;
      }
      case 'BUTTON':
      case 'SELECT':
      case 'TEXTAREA':
        return true;
      default:
        return false;
    }
    return false;
  }, []);

  const attemptFocus = React.useCallback(
    (element: HTMLElement) => {
      if (!isFocusable(element)) {
        return false;
      }

      element.focus();

      // See if the element is now focused.
      // eslint-disable-next-line no-restricted-globals
      return document.activeElement === element;
    },
    [isFocusable],
  );

  // Focuses the next element, according to https://html.spec.whatwg.org/multipage/interaction.html#suitable-sequentially-focusable-area
  // This is a pre-order traversal
  /* istanbul ignore next */
  const focusNextInNode = React.useCallback((element: HTMLElement) => {
    if (!element) {
      return false;
    }
    for (let i = 0; i < element.children.length; i++) {
      const child = element.children[i];
      if (attemptFocus(child) || focusNextInNode(child)) {
        return true;
      }
    }
    return false;
    // Recursive dependency; this is valid anyway, since none of these callbacks depend on props/state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focuses the previous element. This is a _reverse_ pre-order traversal, NOT a post-order traversal
  /* istanbul ignore next */
  const focusPreviousInNode = React.useCallback((element: HTMLElement) => {
    if (!element) {
      return false;
    }
    for (let i = element.children.length - 1; i >= 0; i--) {
      const child = element.children[i];
      if (focusPreviousInNode(child) || attemptFocus(child)) {
        return true;
      }
    }
    return false;
    // See above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusPrevious = React.useCallback(
    (element: HTMLElement) => {
      let previousSibling = element;
      while (previousSibling && !focusPreviousInNode(previousSibling)) {
        previousSibling = previousSibling.previousElementSibling;
      }
    },
    [focusPreviousInNode],
  );

  const focusNext = React.useCallback(
    (element: HTMLElement) => {
      let nextSibling = element;
      while (nextSibling && !focusNextInNode(nextSibling)) {
        nextSibling = nextSibling.nextElementSibling;
      }
    },
    [focusNextInNode],
  );

  const onStartFocus = React.useCallback(() => {
    // Focus event at beginning of focus trap => user is tabbing backwards
    if (end.current) {
      focusPrevious(end.current);
    }
  }, [focusPrevious]);

  const onEndFocus = React.useCallback(() => {
    if (start.current) {
      // Focus event at end of focus trap => user is tabbing forwards
      focusNext(start.current);
    }
  }, [focusNext]);

  const elementType = type || 'div';
  return (
    <>
      {React.createElement(elementType, { onFocus: onStartFocus, tabIndex: 0, ref: (start: any) })}
      <AutoFocus
        disableFocusRestorePoint={disableFocusRestorePoint}
        disableFocusOnMount={disableFocusOnMount}
        disableRestoreFocus={disableRestoreFocus}
      >
        {React.createElement(elementType, { tabIndex: -1 })}
        {children}
      </AutoFocus>
      {React.createElement(elementType, { onFocus: onEndFocus, tabIndex: 0, ref: (end: any) })}
    </>
  );
};

export default FocusTrap;
